import { useState, useEffect, useCallback, useRef } from 'react'
import StepperProgramados from './StepperProgramados'
import Paso0SubirExcelProgramados from './Paso0SubirExcelProgramados'
import Paso1PrevisualizacionProgramados from './Paso1PrevisualizacionProgramados'
import Paso2ProcesandoProgramados from './Paso2ProcesandoProgramados'
import Paso3ReporteProgramados from './Paso3ReporteProgramados'
import { useBulkScheduled } from '../../../../../../../../context/BulkScheduledContext'
import { useGlobalContext } from '../../../../../../../../context/GlobalState'
import { FacturaAPI } from '../../../../../../../../api/facturas.api'
import { Button } from '../../../../../../ui/index'

export default function FacturasProgramadasExcel({ onBack, isModal = false, setModalFooter }) {
  const [activeStep, setActiveStep] = useState(0)
  const [rows, setRows] = useState([]) // Documentos procesados
  const [excelFileName, setExcelFileName] = useState('')
  const onBackRef = useRef(onBack)

  const { currentBusiness, masterEntityData } = useGlobalContext()
  const { isProcessing, isFinished, resetEmission } = useBulkScheduled()

  const api = new FacturaAPI()

  useEffect(() => {
    onBackRef.current = onBack
  }, [onBack])

  const handleBack = useCallback(() => setActiveStep(prev => Math.max(prev - 1, 0)), [])
  const handleNext = useCallback(() => {
    resetEmission()
    setActiveStep(prev => Math.min(prev + 1, 3))
  }, [resetEmission])
  const handleReset = useCallback(() => {
    setActiveStep(0)
    setRows([])
    setExcelFileName('')
    resetEmission()
  }, [resetEmission])
  const handleCloseReport = useCallback(() => {
    handleReset()
    if (onBackRef.current) onBackRef.current()
  }, [handleReset])

  // Sincronizar paso si ya se está procesando o terminó
  useEffect(() => {
    if (!isProcessing && isFinished && activeStep === 0) {
      resetEmission()
    }
  }, [activeStep, isProcessing, isFinished, resetEmission])

  useEffect(() => {
    if (isProcessing) setActiveStep(2)
    else if (isFinished && activeStep !== 0) setActiveStep(3)
  }, [activeStep, isProcessing, isFinished])

  // Manejo dinámico del footer del modal
  useEffect(() => {
    if (isModal && setModalFooter) {
      if (activeStep === 1) {
        setModalFooter(
          <Button
            onClick={handleBack}
            className='text-[10px] bg-primary text-white rounded-full'
            title='Atrás'
          />
        )
      } else if (activeStep === 3) {
        setModalFooter(
          <Button
            onClick={handleCloseReport}
            className='text-[10px] bg-primary text-white rounded-full'
            title='Cerrar'
          />
        )
      } else {
        setModalFooter(null)
      }
    }
  }, [activeStep, handleBack, handleCloseReport, isModal, setModalFooter])

  const onUploadExcel = async ({ documents, fileName }) => {
    resetEmission()
    setExcelFileName(fileName)
    try {
      // 1. Los documentos ya vienen parseados y validados desde Paso 0
      const parsedDocs = documents
      if (!parsedDocs || parsedDocs.length === 0) {
        setRows([])
        return
      }

      // 2. Obtener RUTs únicos para consultar API
      const uniqueRuts = [...new Set(parsedDocs.map(d => d.rut_receptor).filter(Boolean))]

      // 3. Consultar datos de cada RUT
      const entitiesCache = {}
      await Promise.all(
        uniqueRuts.map(async rut => {
          try {
            // getSearchEntityRut devuelve { data: { ... } } o directo el obj
            const res = await api.getSearchEntityRut(rut)
            const entityData = res.data || res
            entitiesCache[rut] = entityData
          } catch (err) {
            console.warn(`No se pudo obtener datos para ${rut}`, err)
            entitiesCache[rut] = null // Marcar como no encontrado
          }
        })
      )

      // 4. Enriquecer documentos con datos de API
      const enrichedDocs = parsedDocs.map(doc => {
        const entity = entitiesCache[doc.rut_receptor]
        if (entity) {
          // Extraer la dirección principal o la primera disponible
          const mainAddress =
            entity.addresses?.find(a => a.is_main_in_sii) || entity.addresses?.[0] || {}

          return {
            ...doc,
            business_name: entity.name || entity.razon_social || entity.business_name || '',
            email: entity.email || '',
            address: mainAddress.address || entity.direccion || entity.address || '',
            district: mainAddress.district?.name || entity.comuna || entity.district || '',
            city: mainAddress.district?.city?.name || entity.ciudad || entity.city || ''
          }
        }
        return doc
      })

      setRows(enrichedDocs)
      setActiveStep(1)
    } catch (error) {
      console.error('Error processing excel', error)
      // ui.setDialog({
      //   open: true,
      //   title: 'Error de Lectura',
      //   body: 'Hubo un problema procesando los datos del archivo.',
      //   btnText: 'Cerrar'
      // })
    } finally {
      // ui.setLoader({ visible: false, text: '' })
    }
  }

  const content = (
    <>
      {activeStep === 0 && (
        <>
          <Paso0SubirExcelProgramados
            onUploadSuccess={onUploadExcel}
            isModal={isModal}
          />

        </>
      )}

      {/* Paso 1 */}
      {activeStep === 1 && (
        <Paso1PrevisualizacionProgramados
          fileName={excelFileName}
          rows={rows}
          onBack={handleBack}
          onContinue={handleNext}
          onDeleteRow={idx => setRows(prev => prev.filter((_, i) => i !== idx))}
          onUpdateRow={(idx, updatedRow) => setRows(prev => {
            const next = [...prev]
            next[idx] = updatedRow
            return next
          })}
        />
      )}

      {/* Paso 2: Procesamiento */}
      {activeStep === 2 && (
        <Paso2ProcesandoProgramados
          documents={rows}
          currentBusiness={currentBusiness}
          masterEntityData={masterEntityData}
          onMinimize={() => {
            if (onBack) onBack() // En este contexto, onBack cierra el modal principal o sale de la vista
          }}
          onFinish={() => setActiveStep(3)}
        />
      )}

      {/* Paso 3: Reporte */}
      {activeStep === 3 && (
        <Paso3ReporteProgramados
          onBack={() => setActiveStep(1)}
          onDone={onBack}
          onReset={handleReset}
        />
      )}
    </>
  )

  if (isModal) {
    return (
      <div className='h-full overflow-hidden rounded-2xl bg-white'>
        <div className='border-b border-slate-200'>
          <StepperProgramados activeStep={activeStep} />
        </div>

        <div className='h-[calc(100%-76px)] overflow-y-auto p-4'>{content}</div>
      </div>
    )
  }

  return (
    <div className='p-8 space-y-6'>
      <StepperProgramados activeStep={activeStep} />
      {content}
    </div>
  )
}
